import React from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { useI18n } from '../../hooks/useI18n';

export const SubmissionEditModal = ({
  open,
  onClose,
  fieldLabels,
  editValues,
  onChange,
  onSubmit,
  saving,
  error,
  success
}) => {
  const { t } = useI18n();
  const entries = Object.entries(editValues || {});

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('requestsEditSubmissionTitle')}
      closeDisabled={saving}
      panelClassName="flex max-h-[min(90vh,720px)] max-w-xl flex-col overflow-hidden p-0"
      contentClassName="mb-0 shrink-0 border-b border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5"
      titleClassName="text-start text-base"
    >
      <form className="flex min-h-0 flex-1 flex-col text-start" onSubmit={onSubmit}>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
          {entries.length ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {entries.map(([slug, value]) => {
                const label = fieldLabels[slug] || slug;
                const normalizedValue = value ?? '';
                const isLongText = typeof normalizedValue === 'string' && normalizedValue.length > 120;

                return (
                  <div key={slug} className={isLongText ? 'sm:col-span-2' : undefined}>
                    <div className="mb-1.5 flex min-w-0 items-center justify-between gap-2">
                      <label className="truncate text-sm font-medium text-neutral-500" htmlFor={`submission-field-${slug}`}>
                        {label}
                      </label>
                      <span className="max-w-[45%] truncate text-[11px] text-neutral-400">{slug}</span>
                    </div>
                    {isLongText ? (
                      <Textarea
                        id={`submission-field-${slug}`}
                        rows={4}
                        className="min-h-[104px] rounded-xl px-3 py-2.5 text-start leading-6"
                        value={normalizedValue}
                        disabled={saving}
                        onChange={(event) => onChange(slug, event.target.value)}
                      />
                    ) : (
                      <Input
                        id={`submission-field-${slug}`}
                        className="rounded-xl px-3 py-2.5 text-start"
                        value={normalizedValue}
                        disabled={saving}
                        onChange={(event) => onChange(slug, event.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-200 px-4 py-10 text-center text-sm text-neutral-500 dark:border-neutral-800">
              {t('requestsNoEditableFields')}
            </div>
          )}

          {error ? (
            <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
              {success}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? t('requestsSaving') : t('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
